import { create, parseDependencies, type MathNode } from 'mathjs';
import { z } from 'zod';
import { isoDaySchema } from './dates';
import { paginatedSchema, paginationQuerySchema } from './pagination';

/** Unidades de medida do PRD §11.11 (CUSTOM = unidade customizada da empresa). */
export const KPI_UNITS = [
  'CURRENCY',
  'PERCENT',
  'UNITS',
  'TONS',
  'LITERS',
  'HOURS',
  'DAYS',
  'CUSTOMERS',
  'TICKETS',
  'AVAILABILITY',
  'INDEX',
  'SCORE',
  'CUSTOM',
] as const;
export type KpiUnitName = (typeof KPI_UNITS)[number];
export const KPI_UNIT_LABELS: Record<KpiUnitName, string> = {
  CURRENCY: 'R$',
  PERCENT: '%',
  UNITS: 'Unidades',
  TONS: 'Toneladas',
  LITERS: 'Litros',
  HOURS: 'Horas',
  DAYS: 'Dias',
  CUSTOMERS: 'Clientes',
  TICKETS: 'Chamados',
  AVAILABILITY: 'Disponibilidade',
  INDEX: 'Índice',
  SCORE: 'Nota',
  CUSTOM: 'Unidade customizada',
};

/**
 * Agregação do acumulado/YTD (DEC-004 item 11) e da consolidação das unidades (T-067, DEC-037). A média
 * ponderada pesa as unidades pelo peso de cada uma; no tempo (períodos e acumulado) é a média simples.
 */
export const KPI_AGGREGATIONS = ['SUM', 'AVERAGE', 'WEIGHTED_AVERAGE', 'LAST'] as const;
export type KpiAggregationName = (typeof KPI_AGGREGATIONS)[number];
export const KPI_AGGREGATION_LABELS: Record<KpiAggregationName, string> = {
  SUM: 'Soma',
  AVERAGE: 'Média',
  WEIGHTED_AVERAGE: 'Média ponderada pelas unidades',
  LAST: 'Último valor',
};
/** Padrão por unidade (DEC-004 item 11): soma para quantidades, média para taxas e notas. */
export const DEFAULT_AGGREGATION: Record<Exclude<KpiUnitName, 'CUSTOM'>, KpiAggregationName> = {
  CURRENCY: 'SUM',
  PERCENT: 'AVERAGE',
  UNITS: 'SUM',
  TONS: 'SUM',
  LITERS: 'SUM',
  HOURS: 'SUM',
  DAYS: 'SUM',
  CUSTOMERS: 'SUM',
  TICKETS: 'SUM',
  AVAILABILITY: 'AVERAGE',
  INDEX: 'AVERAGE',
  SCORE: 'AVERAGE',
};

export const KPI_PERIODICITIES = [
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'SEMIANNUAL',
  'ANNUAL',
  'CUSTOM',
] as const;
export type KpiPeriodicityName = (typeof KPI_PERIODICITIES)[number];
export const KPI_PERIODICITY_LABELS: Record<KpiPeriodicityName, string> = {
  DAILY: 'Diária',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
  CUSTOM: 'Personalizada',
};

export const KPI_DATA_SOURCES = ['MANUAL', 'IMPORTED', 'CALCULATED'] as const;
export type KpiDataSourceName = (typeof KPI_DATA_SOURCES)[number];
export const KPI_DATA_SOURCE_LABELS: Record<KpiDataSourceName, string> = {
  MANUAL: 'Manual',
  IMPORTED: 'Importado',
  CALCULATED: 'Calculado',
};

export const KPI_DIRECTIONS = ['HIGHER_BETTER', 'LOWER_BETTER', 'RANGE', 'CUSTOM_FORMULA'] as const;
export type KpiDirectionName = (typeof KPI_DIRECTIONS)[number];
export const KPI_DIRECTION_LABELS: Record<KpiDirectionName, string> = {
  HIGHER_BETTER: 'Quanto maior, melhor',
  LOWER_BETTER: 'Quanto menor, melhor',
  RANGE: 'Faixa ideal',
  CUSTOM_FORMULA: 'Fórmula personalizada',
};

/** Classificação do KPI (todo KPI mede ao menos um objetivo — PRD §5.1, RN-01, RN-28). */
export const KPI_KINDS = ['STRATEGIC', 'OPERATIONAL'] as const;
export type KpiKindName = (typeof KPI_KINDS)[number];
export const KPI_KIND_LABELS: Record<KpiKindName, string> = {
  STRATEGIC: 'Estratégico',
  OPERATIONAL: 'Operacional',
};

/** Regra de semáforo: a da empresa (DEC-004 item 4) ou uma própria do KPI. */
export const KPI_TRAFFIC_MODES = ['COMPANY', 'CUSTOM'] as const;
export type KpiTrafficModeName = (typeof KPI_TRAFFIC_MODES)[number];

// ---------------------------------------------------------------------------------------------
// Fórmula personalizada de performance (direção "fórmula personalizada")

// Só o parser do mathjs (DEC-005): a fórmula é conferida aqui; quem calcula é o motor (T-061).
const { parse } = create({ parseDependencies: parseDependencies! });

/** Variáveis que a fórmula de performance pode usar. */
export const PERFORMANCE_FORMULA_VARIABLES = ['realizado', 'meta'] as const;
const FORMULA_FUNCTIONS = new Set(['min', 'max', 'abs', 'round', 'sqrt']);
const FORMULA_OPERATORS = new Set(['+', '-', '*', '/', '^']);

/**
 * Confere uma expressão com as operações liberadas (números, + − × ÷ ^, parênteses e
 * min/max/abs/round/sqrt) e só as variáveis permitidas. Devolve a mensagem de erro ou null; nada é
 * executado aqui.
 */
function expressionError(
  expression: string,
  variables: readonly string[],
  messages: { unknown: (name: string) => string; noVariable: string },
): string | null {
  let root: MathNode;
  try {
    root = parse(expression);
  } catch {
    return 'Fórmula com erro de escrita.';
  }
  let error: string | null = null;
  let usesVariable = false;
  root.traverse((node, _path, parent) => {
    if (error) return;
    switch (node.type) {
      case 'ConstantNode':
        if (typeof (node as unknown as { value: unknown }).value !== 'number')
          error = 'Use só números na fórmula.';
        return;
      case 'ParenthesisNode':
        return;
      case 'OperatorNode': {
        const op = (node as unknown as { op: string; fn: string }).op;
        if (!FORMULA_OPERATORS.has(op)) error = `Operação não permitida: ${op}`;
        return;
      }
      case 'FunctionNode': {
        const name = (node as unknown as { fn: { name?: string } }).fn.name ?? '';
        if (!FORMULA_FUNCTIONS.has(name)) error = `Função não permitida: ${name}`;
        return;
      }
      case 'SymbolNode': {
        const name = (node as unknown as { name: string }).name;
        // O nome de uma função chamada aparece como símbolo filho do FunctionNode.
        if (parent?.type === 'FunctionNode' && (parent as unknown as { fn: MathNode }).fn === node) return;
        if (!variables.includes(name)) error = messages.unknown(name);
        else usesVariable = true;
        return;
      }
      default:
        error = 'Fórmula com elemento não permitido.';
    }
  });
  if (error) return error;
  if (!usesVariable) return messages.noVariable;
  return null;
}

/**
 * Confere a fórmula de performance (ex.: `realizado / meta * 100`): só números, as variáveis
 * `realizado` e `meta`, + − × ÷ ^, parênteses e min/max/abs/round/sqrt. Devolve a mensagem de erro
 * (ou null se estiver certa). Nada é executado aqui.
 */
export function performanceFormulaError(formula: string): string | null {
  return expressionError(formula, PERFORMANCE_FORMULA_VARIABLES, {
    unknown: (name) => `Use só as variáveis realizado e meta (encontrado: ${name}).`,
    noVariable: 'A fórmula precisa usar realizado ou meta.',
  });
}

// ---------------------------------------------------------------------------------------------
// Fórmula de cálculo (KPI de origem "calculado", T-065): referencia outros KPIs pelo código

/** Referência a outro KPI na fórmula de cálculo: o código entre colchetes, ex.: [RECEITA]. */
const CALCULATION_REFERENCE = /\[\s*([A-Za-z][A-Za-z0-9_]*)\s*\]/g;

/** Códigos referenciados pela fórmula de cálculo (maiúsculos, sem repetição, na ordem em que aparecem). */
export function calculationFormulaReferences(formula: string): string[] {
  return [...new Set([...formula.matchAll(CALCULATION_REFERENCE)].map((m) => m[1]!.toUpperCase()))];
}

/** A fórmula com cada [CÓDIGO] trocado por uma variável (k0, k1…), para o parser do mathjs. */
export function calculationFormulaExpression(formula: string): {
  expression: string;
  variables: Record<string, string>;
} {
  const variables: Record<string, string> = {};
  calculationFormulaReferences(formula).forEach((code, i) => {
    variables[code] = `k${i}`;
  });
  const expression = formula.replace(
    CALCULATION_REFERENCE,
    (_m, code: string) => variables[code.toUpperCase()]!,
  );
  return { expression, variables };
}

/**
 * Confere a fórmula de cálculo (ex.: `[RECEITA] / [CLIENTES]`): os KPIs entram pelo código entre
 * colchetes; as operações são as mesmas da fórmula de performance. Referências (existência, escopo e
 * ciclos) são conferidas na API.
 */
export function calculationFormulaError(formula: string): string | null {
  const { expression, variables } = calculationFormulaExpression(formula);
  return expressionError(expression, Object.values(variables), {
    unknown: (name) =>
      `Referencie os indicadores pelo código entre colchetes, ex.: [RECEITA] (encontrado: ${name}).`,
    noVariable: 'Referencie ao menos um indicador pelo código entre colchetes, ex.: [RECEITA].',
  });
}

// ---------------------------------------------------------------------------------------------
// Unidades customizadas da empresa

export const kpiCustomUnitSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  symbol: z.string().nullable(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  archivedAt: z.string().nullable(),
});
export type KpiCustomUnit = z.infer<typeof kpiCustomUnitSchema>;
export const kpiCustomUnitListSchema = z.object({ items: z.array(kpiCustomUnitSchema) });
export const kpiCustomUnitInputSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da unidade.').max(60, 'Nome longo demais.'),
  symbol: z
    .string()
    .trim()
    .max(10, 'Símbolo longo demais.')
    .nullable()
    .optional()
    .transform((v) => v || null),
  aggregation: z.enum(KPI_AGGREGATIONS, { message: 'Escolha como o acumulado é calculado.' }),
});
export type KpiCustomUnitInput = z.input<typeof kpiCustomUnitInputSchema>;

// ---------------------------------------------------------------------------------------------
// KPI

const ref = z.object({ id: z.string().uuid(), name: z.string() });

export const kpiSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  kind: z.enum(KPI_KINDS),
  perspective: ref,
  department: ref,
  owner: ref,
  unit: z.enum(KPI_UNITS),
  customUnit: ref.extend({ symbol: z.string().nullable() }).nullable(),
  periodicity: z.enum(KPI_PERIODICITIES),
  customPeriodDays: z.number().int().nullable(),
  dataSource: z.enum(KPI_DATA_SOURCES),
  direction: z.enum(KPI_DIRECTIONS),
  rangeLower: z.number().nullable(),
  rangeUpper: z.number().nullable(),
  performanceFormula: z.string().nullable(),
  /** Origem "calculado": a fórmula com os KPIs de entrada (T-065). */
  calculationFormula: z.string().nullable(),
  formula: z.string(),
  aggregation: z.enum(KPI_AGGREGATIONS),
  validFrom: z.string(),
  validTo: z.string().nullable(),
  trafficMode: z.enum(KPI_TRAFFIC_MODES),
  greenMin: z.number().nullable(),
  yellowMin: z.number().nullable(),
  /**
   * Objetivos que o KPI mede, com o peso dele em cada um (RN-08). Peso vazio = sem peso definido: no
   * cálculo (T-061) os KPIs sem peso de um objetivo dividem igualmente o que sobra.
   */
  objectives: z.array(ref.extend({ weight: z.number().nullable(), archived: z.boolean() })),
  /** Quantos objetivos ligados ficam fora do escopo de área de quem pede (sem nomes — DEC-016). */
  hiddenObjectives: z.number().int(),
  archivedAt: z.string().nullable(),
});
export type Kpi = z.infer<typeof kpiSchema>;
export const kpiListSchema = paginatedSchema(kpiSchema);

export const kpiListQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100, 'Busca longa demais.').optional(),
  perspectiveId: z.string().uuid().optional(),
  objectiveId: z.string().uuid().optional(),
  departmentId: z.string().uuid().optional(),
  ownerId: z.string().uuid().optional(),
  kind: z.enum(KPI_KINDS).optional(),
  direction: z.enum(KPI_DIRECTIONS).optional(),
  dataSource: z.enum(KPI_DATA_SOURCES).optional(),
  periodicity: z.enum(KPI_PERIODICITIES).optional(),
  archived: z.enum(['true', 'false']).optional(),
});
export type KpiListQuery = z.input<typeof kpiListQuerySchema>;

const percent = (label: string) =>
  z.coerce
    .number({ message: `Informe ${label} em números.` })
    .min(0, `${label[0]!.toUpperCase()}${label.slice(1)} vai de 0 a 100.`)
    .max(100, `${label[0]!.toUpperCase()}${label.slice(1)} vai de 0 a 100.`)
    .multipleOf(0.01, 'Use no máximo duas casas decimais.');
/** Limite de faixa ideal: cabe na coluna DECIMAL(20,6) (menos de 10^14, até 6 casas). */
const RANGE_LIMIT = 1e14;
const decimal = z.coerce
  .number({ message: 'Informe um número.' })
  .finite('Informe um número.')
  .refine((v) => Math.abs(v) < RANGE_LIMIT, 'Número grande demais (até 99 trilhões).')
  .refine((v) => Number.isInteger(Math.round(v * 1e6 * 1e3) / 1e3), 'Use no máximo seis casas decimais.');

/** Código do KPI (referência nas fórmulas de KPI calculado — T-065): MAIÚSCULAS, números e _. */
export const KPI_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;

/** Formulário completo do KPI (criar e substituir com PUT). */
export const kpiInputSchema = z
  .object({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        KPI_CODE_PATTERN,
        'Use de 2 a 30 caracteres: letras maiúsculas, números e _ (começando por letra).',
      ),
    name: z.string().trim().min(3, 'Informe o nome do indicador.').max(160, 'Nome longo demais.'),
    description: z
      .string()
      .trim()
      .min(3, 'Descreva o que o indicador mede.')
      .max(2000, 'A descrição pode ter no máximo 2000 caracteres.'),
    kind: z.enum(KPI_KINDS).default('STRATEGIC'),
    perspectiveId: z.string({ message: 'Escolha a perspectiva.' }).uuid('Escolha a perspectiva.'),
    departmentId: z.string({ message: 'Escolha a área.' }).uuid('Escolha a área.'),
    ownerId: z.string({ message: 'Escolha o responsável.' }).uuid('Escolha o responsável.'),
    unit: z.enum(KPI_UNITS, { message: 'Escolha a unidade de medida.' }),
    customUnitId: z
      .string()
      .uuid('Escolha a unidade customizada.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    periodicity: z.enum(KPI_PERIODICITIES, { message: 'Escolha a periodicidade.' }),
    customPeriodDays: z.coerce
      .number()
      .int('Use um número inteiro de dias.')
      .min(1, 'O intervalo vai de 1 a 3660 dias.')
      .max(3660, 'O intervalo vai de 1 a 3660 dias.')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    dataSource: z.enum(KPI_DATA_SOURCES, { message: 'Escolha a fonte de dados.' }),
    direction: z.enum(KPI_DIRECTIONS, { message: 'Escolha a direção desejada.' }),
    rangeLower: decimal
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    rangeUpper: decimal
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    performanceFormula: z
      .string()
      .trim()
      .max(500, 'Fórmula longa demais.')
      .nullable()
      .optional()
      .transform((v) => v || null),
    calculationFormula: z
      .string()
      .trim()
      .max(1000, 'Fórmula longa demais.')
      .nullable()
      .optional()
      .transform((v) => v || null),
    formula: z
      .string()
      .trim()
      .min(1, 'Explique como o indicador é calculado.')
      .max(1000, 'A fórmula pode ter no máximo 1000 caracteres.'),
    aggregation: z
      .enum(KPI_AGGREGATIONS)
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    validFrom: isoDaySchema,
    validTo: isoDaySchema
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    trafficMode: z.enum(KPI_TRAFFIC_MODES).default('COMPANY'),
    greenMin: percent('o mínimo do verde')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    yellowMin: percent('o mínimo do amarelo')
      .nullable()
      .optional()
      .transform((v) => v ?? null),
    objectives: z
      .array(
        z.object({
          objectiveId: z.string().uuid(),
          weight: percent('o peso')
            .nullable()
            .optional()
            .transform((v) => v ?? null),
        }),
      )
      // Sem .min(1): quem tem escopo por área pode enxergar só parte dos vínculos. RN-01 é conferida
      // depois de juntar os vínculos ocultos (API) e, no formulário, contando os ocultos.
      .max(30, 'No máximo 30 objetivos.')
      .refine((l) => new Set(l.map((o) => o.objectiveId)).size === l.length, 'Objetivo repetido.')
      .default([]),
  })
  .superRefine((v, ctx) => {
    const issue = (path: string, message: string) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (v.unit === 'CUSTOM' && !v.customUnitId) issue('customUnitId', 'Escolha a unidade customizada.');
    if (v.periodicity === 'CUSTOM' && !v.customPeriodDays) {
      issue('customPeriodDays', 'Informe o intervalo em dias.');
    }
    if (v.direction === 'RANGE') {
      if (v.rangeLower === null) issue('rangeLower', 'Informe o limite inferior da faixa.');
      if (v.rangeUpper === null) issue('rangeUpper', 'Informe o limite superior da faixa.');
      if (v.rangeLower !== null && v.rangeUpper !== null && v.rangeLower > v.rangeUpper) {
        issue('rangeUpper', 'O limite superior precisa ser maior ou igual ao inferior.');
      }
    }
    if (v.direction === 'CUSTOM_FORMULA') {
      if (!v.performanceFormula) issue('performanceFormula', 'Escreva a fórmula de performance.');
      else {
        const error = performanceFormulaError(v.performanceFormula);
        if (error) issue('performanceFormula', error);
      }
    }
    if (v.dataSource === 'CALCULATED') {
      if (!v.calculationFormula) issue('calculationFormula', 'Escreva a fórmula de cálculo.');
      else {
        const error = calculationFormulaError(v.calculationFormula);
        if (error) issue('calculationFormula', error);
        else if (calculationFormulaReferences(v.calculationFormula).includes(v.code)) {
          issue('calculationFormula', 'A fórmula não pode usar o próprio indicador.');
        }
      }
      // O cálculo usa o realizado do mês de cada entrada (DEC-035).
      if (v.periodicity !== 'MONTHLY') {
        issue('periodicity', 'Indicador calculado é mensal: a fórmula usa o realizado do mês das entradas.');
      }
    }
    if (v.validTo && v.validTo < v.validFrom) {
      issue('validTo', 'O fim da vigência precisa ser igual ou posterior ao início.');
    }
    if (v.trafficMode === 'CUSTOM') {
      if (v.greenMin === null) issue('greenMin', 'Informe a partir de quanto é verde.');
      if (v.yellowMin === null) issue('yellowMin', 'Informe a partir de quanto é amarelo.');
      if (v.greenMin !== null && v.yellowMin !== null && v.yellowMin > v.greenMin) {
        issue('yellowMin', 'O amarelo começa abaixo (ou no mesmo ponto) do verde.');
      }
    }
  });
export type KpiInput = z.input<typeof kpiInputSchema>;

/** Opções do formulário de KPI. */
export const kpiOptionsSchema = z.object({
  perspectives: z.array(ref),
  objectives: z.array(ref.extend({ perspectiveId: z.string().uuid() })),
  departments: z.array(ref.extend({ parentId: z.string().uuid().nullable() })),
  owners: z.array(ref),
  customUnits: z.array(kpiCustomUnitSchema),
  /** KPIs ativos do projeto no escopo de quem edita: os que a fórmula de cálculo pode usar (T-065). */
  kpis: z.array(ref.extend({ code: z.string() })),
});
export type KpiOptions = z.infer<typeof kpiOptionsSchema>;

/** Rótulo da unidade do KPI ("R$", "%", "Clientes" ou a customizada). */
export function kpiUnitLabel(k: Pick<Kpi, 'unit' | 'customUnit'>): string {
  if (k.unit === 'CUSTOM') return k.customUnit?.symbol || k.customUnit?.name || KPI_UNIT_LABELS.CUSTOM;
  return KPI_UNIT_LABELS[k.unit];
}
